export type FeedbackIdentityKind =
	| "ANONYMOUS"
	| "HOSTED_LOGIN"
	| "MERCHANT_USER"
	| "UNKNOWN"

export type FeedbackActor = {
	identityKind?: FeedbackIdentityKind | Lowercase<FeedbackIdentityKind>
	anonymousUserId?: string | null
	loginExternalUserId?: string | null
	merchantUserId?: string | null
	contactEmail?: string | null
	contactPhone?: string | null
	displayName?: string | null
}

export type FeedbackCategoryOption = {
	value: string
	label: string
}

export type FeedbackWidgetOptions = {
	appId: string
	apiBaseUrl?: string
	actor?: FeedbackActor
	metadata?: Record<string, unknown>
	categories?: FeedbackCategoryOption[]
	launcherText?: string
	panelTitle?: string
	panelSubtitle?: string
	placeholder?: string
	contactPlaceholder?: string
	onThreadCreated?: (threadId: string) => void
	onMessageSent?: (messageId: string) => void
	onError?: (error: Error) => void
}

export type FeedbackWidgetController = {
	open: () => void
	close: () => void
	destroy: () => void
}

type FeedbackMessage = {
	id: string
	senderType: string
	content: string
	translatedContent?: string | null
	createdAt: string
}

type ThreadState = {
	threadId: string
	clientToken: string
	lastMessageId?: string
	status: string
	category?: string
	contentPreview?: string
	createdAt?: string
}

const DEFAULT_CATEGORIES: FeedbackCategoryOption[] = [
	{ value: "BUG", label: "问题反馈" },
	{ value: "PAYMENT", label: "支付问题" },
	{ value: "LOGIN", label: "登录问题" },
	{ value: "SUGGESTION", label: "功能建议" },
	{ value: "OTHER", label: "其他" },
]

const STYLE_ID = "youidian-feedback-widget-style"
const MAX_IMAGES_PER_MESSAGE = 4
const DEFAULT_PANEL_TITLE = "反馈"
const DEFAULT_PANEL_SUBTITLE = "提交问题、建议或使用反馈。"
const DEFAULT_ATTACHMENTS_LABEL = "支持粘贴、拖入或上传截图，最多 4 张"
const FEEDBACK_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.6 8.6 0 0 1-7.7 4.7 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.2a8.5 8.5 0 0 1-1-4.1 8.6 8.6 0 0 1 8.6-8.6 8.5 8.5 0 0 1 8.5 8.4Z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.4 10.2h7.2M8.4 13.5h4.7" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>`
const BUG_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 8.5a4 4 0 0 1 8 0v6a4 4 0 0 1-8 0v-6Z" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M5 9h3m8 0h3M5.5 15H8m8 0h2.5M7 5.5 5.5 4M17 5.5 18.5 4M12 4.5V2.8M12 20.5v-12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`
const NOTE_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3.5h7l3.5 3.5v13.5H7A2.5 2.5 0 0 1 4.5 18V6A2.5 2.5 0 0 1 7 3.5Z" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M14 3.8V7h3.2M8 11h8M8 15h6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`
const MAIL_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 6.5h15v11h-15v-11Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="m5 7 7 6 7-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`
const IMAGE_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5h14v14H5V5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="m7.5 16 3.2-3.2 2.4 2.4 1.7-1.7L18 16.8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M15.8 8.2h.01" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>`
const CHEVRON_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`

function ensureStyle() {
	if (typeof document === "undefined") return
	if (document.getElementById(STYLE_ID)) return
	const style = document.createElement("style")
	style.id = STYLE_ID
	style.textContent = `
.yd-feedback-root,.yd-feedback-root *{box-sizing:border-box}
.yd-feedback-root{--yd-feedback-bg:hsl(var(--background,0 0% 100%));--yd-feedback-fg:hsl(var(--foreground,240 10% 3.9%));--yd-feedback-card:hsl(var(--card,0 0% 100%));--yd-feedback-card-fg:hsl(var(--card-foreground,240 10% 3.9%));--yd-feedback-muted:hsl(var(--muted,240 4.8% 95.9%));--yd-feedback-muted-fg:hsl(var(--muted-foreground,240 3.8% 46.1%));--yd-feedback-border:hsl(var(--border,240 5.9% 90%));--yd-feedback-primary:hsl(var(--primary,142 76% 36%));--yd-feedback-primary-fg:hsl(var(--primary-foreground,0 0% 100%));--yd-feedback-ring:hsl(var(--ring,142 76% 36%));font:14px/1.5 Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:var(--yd-feedback-fg)}
.yd-feedback-root button,.yd-feedback-root input,.yd-feedback-root textarea{font:inherit}
.yd-feedback-launcher{position:fixed;right:22px;bottom:22px;z-index:2147483000;width:54px;height:54px;display:grid;place-items:center;border:1px solid color-mix(in srgb,var(--yd-feedback-primary) 24%,var(--yd-feedback-border));border-radius:999px;background:var(--yd-feedback-primary);color:var(--yd-feedback-primary-fg);box-shadow:0 18px 45px color-mix(in srgb,var(--yd-feedback-primary) 22%,transparent);cursor:pointer;transition:transform .18s ease,box-shadow .18s ease,background .18s ease}
.yd-feedback-launcher:hover{transform:translateY(-2px);box-shadow:0 22px 56px color-mix(in srgb,var(--yd-feedback-primary) 30%,transparent)}
.yd-feedback-launcher:focus-visible{outline:3px solid color-mix(in srgb,var(--yd-feedback-ring) 32%,transparent);outline-offset:3px}
.yd-feedback-launcher svg{width:27px;height:27px}
.yd-feedback-panel{position:fixed;inset:auto 22px 86px auto;z-index:2147483000;width:min(760px,calc(100vw - 44px));height:min(560px,calc(100vh - 108px));display:none;grid-template-rows:auto minmax(0,1fr);overflow:hidden;border:1px solid var(--yd-feedback-border);border-radius:18px;background:var(--yd-feedback-card);color:var(--yd-feedback-card-fg);box-shadow:0 24px 70px rgba(15,23,42,.18)}
.yd-feedback-panel[data-open="true"]{display:grid}
.yd-feedback-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:18px 20px 16px;border-bottom:1px solid var(--yd-feedback-border);background:var(--yd-feedback-card)}
.yd-feedback-title{margin:0;font-size:22px;line-height:1.15;font-weight:760;letter-spacing:0;color:var(--yd-feedback-fg)}
.yd-feedback-subtitle{margin:8px 0 0;color:var(--yd-feedback-muted-fg);font-size:13px;line-height:1.45}
.yd-feedback-close{display:grid;place-items:center;width:34px;height:34px;border:0;border-radius:10px;background:transparent;color:var(--yd-feedback-muted-fg);cursor:pointer;transition:background .16s ease,color .16s ease}
.yd-feedback-close:hover{background:var(--yd-feedback-muted);color:var(--yd-feedback-fg)}
.yd-feedback-close svg{width:24px;height:24px}
.yd-feedback-shell{display:grid;min-height:0;grid-template-columns:230px minmax(0,1fr)}
.yd-feedback-sidebar{min-width:0;border-right:1px solid var(--yd-feedback-border);background:var(--yd-feedback-muted)}
.yd-feedback-sidebar-toolbar{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 14px;border-bottom:1px solid var(--yd-feedback-border)}
.yd-feedback-sidebar-title{display:flex;align-items:center;gap:9px;color:var(--yd-feedback-fg);font-size:15px;font-weight:720}
.yd-feedback-sidebar-title svg{width:20px;height:20px;color:var(--yd-feedback-primary)}
.yd-feedback-icon-button{display:grid;place-items:center;width:34px;height:34px;border:1px solid var(--yd-feedback-border);border-radius:999px;background:var(--yd-feedback-card);color:var(--yd-feedback-fg);cursor:pointer;transition:background .16s ease,border-color .16s ease}
.yd-feedback-icon-button:hover{background:color-mix(in srgb,var(--yd-feedback-primary) 8%,var(--yd-feedback-card));border-color:color-mix(in srgb,var(--yd-feedback-primary) 38%,var(--yd-feedback-border))}
.yd-feedback-icon-button svg{width:18px;height:18px}
.yd-feedback-thread-list{padding:12px}
.yd-feedback-thread-card{display:none;border:1px solid var(--yd-feedback-border);border-radius:14px;background:var(--yd-feedback-card);padding:12px}
.yd-feedback-thread-card[data-visible="true"]{display:block}
.yd-feedback-thread-badges{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:10px}
.yd-feedback-badge{display:inline-flex;align-items:center;border:1px solid var(--yd-feedback-border);border-radius:999px;background:var(--yd-feedback-muted);color:var(--yd-feedback-fg);padding:4px 9px;font-size:11px;font-weight:700;line-height:1}
.yd-feedback-badge[data-status="true"]{border-color:color-mix(in srgb,var(--yd-feedback-primary) 45%,var(--yd-feedback-border));background:color-mix(in srgb,var(--yd-feedback-primary) 12%,var(--yd-feedback-card));color:var(--yd-feedback-primary)}
.yd-feedback-thread-preview{margin:0;color:var(--yd-feedback-fg);font-size:13px;line-height:1.45;word-break:break-word}
.yd-feedback-thread-meta{display:flex;justify-content:space-between;gap:10px;margin-top:10px;color:var(--yd-feedback-muted-fg);font-size:11px}
.yd-feedback-empty-thread{border:1px dashed var(--yd-feedback-border);border-radius:14px;padding:14px;color:var(--yd-feedback-muted-fg);font-size:12px}
.yd-feedback-main{min-width:0;min-height:0;background:var(--yd-feedback-card);overflow:hidden}
.yd-feedback-form{height:100%;display:grid;grid-template-rows:auto auto auto minmax(0,1fr) auto auto;gap:14px;padding:16px 18px 0}
.yd-feedback-row{display:grid;gap:14px;grid-template-columns:180px minmax(0,1fr)}
.yd-feedback-field{display:flex;min-width:0;flex-direction:column;gap:8px}
.yd-feedback-label{display:flex;align-items:center;gap:8px;color:var(--yd-feedback-fg);font-size:13px;font-weight:720}
.yd-feedback-label svg{width:17px;height:17px;color:var(--yd-feedback-fg)}
.yd-feedback-input,.yd-feedback-textarea{width:100%;border:1px solid var(--yd-feedback-border);border-radius:13px;background:var(--yd-feedback-card);color:var(--yd-feedback-fg);outline:none;transition:border-color .16s ease,box-shadow .16s ease,background .16s ease}
.yd-feedback-input{height:40px;padding:0 13px;font-size:14px}
.yd-feedback-textarea{min-height:118px;padding:13px 14px;font-size:14px;resize:none}
.yd-feedback-input::placeholder,.yd-feedback-textarea::placeholder{color:var(--yd-feedback-muted-fg)}
.yd-feedback-input:focus,.yd-feedback-textarea:focus{border-color:var(--yd-feedback-ring);box-shadow:0 0 0 3px color-mix(in srgb,var(--yd-feedback-ring) 18%,transparent);background:var(--yd-feedback-card)}
.yd-feedback-compose-box{overflow:hidden;border:1px solid var(--yd-feedback-border);border-radius:15px;background:var(--yd-feedback-card)}
.yd-feedback-compose-box .yd-feedback-textarea{border:0;border-radius:0;box-shadow:none;background:transparent}
.yd-feedback-attachment-row{display:flex;align-items:center;justify-content:space-between;gap:12px;border-top:1px solid var(--yd-feedback-border);padding:9px 12px;color:var(--yd-feedback-muted-fg)}
.yd-feedback-file-trigger{display:inline-flex;align-items:center;gap:8px;border:0;background:transparent;color:var(--yd-feedback-fg);cursor:pointer;padding:7px 8px;border-radius:10px}
.yd-feedback-file-trigger:hover{background:var(--yd-feedback-muted)}
.yd-feedback-file-trigger svg{width:18px;height:18px}
.yd-feedback-file-input{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;clip-path:inset(50%)}
.yd-feedback-files{min-width:0;text-align:right;color:var(--yd-feedback-muted-fg);font-size:12px}
.yd-feedback-select{position:relative;width:142px}
.yd-feedback-select-button{width:100%;height:40px;display:flex;align-items:center;justify-content:space-between;gap:10px;border:1px solid var(--yd-feedback-border);border-radius:13px;background:var(--yd-feedback-card);color:var(--yd-feedback-fg);padding:0 12px;cursor:pointer}
.yd-feedback-select-value{display:flex;align-items:center;gap:8px;font-size:14px;font-weight:700}
.yd-feedback-select-value svg{width:18px;height:18px;color:var(--yd-feedback-muted-fg)}
.yd-feedback-select-chevron{width:17px;height:17px;color:var(--yd-feedback-muted-fg)}
.yd-feedback-select-menu{position:absolute;left:0;top:calc(100% + 7px);z-index:2;display:none;min-width:176px;max-height:220px;overflow:auto;border:1px solid var(--yd-feedback-border);border-radius:13px;background:var(--yd-feedback-card);box-shadow:0 18px 45px rgba(15,23,42,.16);padding:5px}
.yd-feedback-select[data-open="true"] .yd-feedback-select-menu{display:block}
.yd-feedback-select-option{width:100%;min-height:38px;display:flex;align-items:center;gap:8px;border:0;border-radius:10px;background:transparent;color:var(--yd-feedback-fg);text-align:left;padding:9px 10px;cursor:pointer}
.yd-feedback-select-option svg{width:18px;height:18px;flex:0 0 18px;color:var(--yd-feedback-muted-fg)}
.yd-feedback-select-option span{line-height:1.2;white-space:nowrap}
.yd-feedback-select-option:hover,.yd-feedback-select-option[data-selected="true"]{background:color-mix(in srgb,var(--yd-feedback-primary) 10%,var(--yd-feedback-card));color:var(--yd-feedback-fg)}
.yd-feedback-messages{display:flex;min-height:0;flex-direction:column;gap:8px;overflow:auto}
.yd-feedback-message{border:1px solid var(--yd-feedback-border);border-radius:12px;padding:9px 10px;background:var(--yd-feedback-muted)}
.yd-feedback-message[data-admin="true"]{background:color-mix(in srgb,var(--yd-feedback-primary) 10%,var(--yd-feedback-card));border-color:color-mix(in srgb,var(--yd-feedback-primary) 24%,var(--yd-feedback-border))}
.yd-feedback-message-meta{margin-bottom:4px;color:var(--yd-feedback-muted-fg);font-size:11px}
.yd-feedback-message-content{color:var(--yd-feedback-fg);font-size:12px;line-height:1.45;word-break:break-word}
.yd-feedback-actions{position:sticky;bottom:0;display:flex;align-items:center;justify-content:flex-end;gap:12px;margin:0 -18px;padding:12px 18px 16px;border-top:1px solid var(--yd-feedback-border);background:var(--yd-feedback-card)}
.yd-feedback-submit{min-width:104px;height:44px;border:0;border-radius:13px;background:var(--yd-feedback-primary);color:var(--yd-feedback-primary-fg);padding:0 18px;font-size:14px;font-weight:800;cursor:pointer;box-shadow:0 12px 24px color-mix(in srgb,var(--yd-feedback-primary) 18%,transparent);transition:background .16s ease,transform .16s ease}
.yd-feedback-submit:hover{transform:translateY(-1px)}
.yd-feedback-submit:disabled{opacity:.55;cursor:not-allowed;transform:none}
.yd-feedback-secondary{height:38px;border:0;border-radius:12px;background:transparent;color:var(--yd-feedback-fg);padding:0 12px;font-size:14px;font-weight:760;cursor:pointer}
.yd-feedback-secondary:hover{background:var(--yd-feedback-muted)}
.yd-feedback-error{min-height:18px;color:#fb7185;font-size:13px}
@media (max-width:760px){
	.yd-feedback-panel{inset:auto 14px 78px 14px;width:auto;height:min(560px,calc(100vh - 96px));border-radius:16px}
	.yd-feedback-header{padding:16px 16px 14px}
	.yd-feedback-title{font-size:20px}
	.yd-feedback-shell{grid-template-columns:1fr}
	.yd-feedback-sidebar{display:none}
	.yd-feedback-form{padding:14px 14px 0;gap:12px}
	.yd-feedback-row{grid-template-columns:1fr}
	.yd-feedback-actions{justify-content:space-between}
}
`
	document.head.appendChild(style)
}

function getBaseUrl(options: FeedbackWidgetOptions) {
	return (options.apiBaseUrl || "https://pay.imgto.link").replace(/\/$/, "")
}

function safeJson(value: unknown) {
	try {
		return JSON.stringify(value)
	} catch {
		return "{}"
	}
}

function escapeHtml(value: string) {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;")
}

function getInitialContact(actor?: FeedbackActor) {
	return actor?.contactEmail || actor?.contactPhone || ""
}

function formatCategoryLabel(
	value?: string,
	categories: FeedbackCategoryOption[] = DEFAULT_CATEGORIES,
) {
	const matched = categories.find((item) => item.value === value)
	return matched?.label || value || DEFAULT_CATEGORIES[0]?.label || "反馈"
}

function formatThreadStatus(value?: string) {
	const statusMap: Record<string, string> = {
		OPEN: "已提交",
		WAITING_ADMIN: "等待回复",
		WAITING_USER: "待补充",
		RESOLVED: "已处理",
		CLOSED: "已关闭",
	}
	return statusMap[value || ""] || value || "已提交"
}

function formatSenderType(value: string) {
	if (value === "END_USER") return "用户"
	if (value === "ADMIN") return "管理员"
	return "系统"
}

function formatThreadTime(value?: string) {
	if (!value) return ""
	return new Date(value).toLocaleString()
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
	const response = await fetch(url, {
		...init,
		headers: {
			Accept: "application/json",
			...(init?.body ? { "Content-Type": "application/json" } : {}),
			...(init?.headers || {}),
		},
	})
	const payload = await response.json().catch(() => null)
	if (!response.ok || payload?.code !== 200) {
		throw new Error(payload?.message || response.statusText || "Request failed")
	}
	return payload.data as T
}

function collectMetadata(options: FeedbackWidgetOptions) {
	const page =
		typeof window === "undefined"
			? {}
			: {
					pageUrl: window.location.href,
					pageTitle: document.title,
					userAgent: navigator.userAgent,
				}
	return { ...page, ...(options.metadata || {}) }
}

async function compressImage(file: File): Promise<File> {
	if (typeof document === "undefined" || !file.type.startsWith("image/")) {
		return file
	}
	const bitmap = await createImageBitmap(file)
	const maxSize = 1600
	const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height))
	const canvas = document.createElement("canvas")
	canvas.width = Math.max(1, Math.round(bitmap.width * scale))
	canvas.height = Math.max(1, Math.round(bitmap.height * scale))
	const ctx = canvas.getContext("2d")
	if (!ctx) return file
	ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
	const blob = await new Promise<Blob | null>((resolve) => {
		canvas.toBlob(resolve, "image/webp", 0.82)
	})
	if (!blob) return file
	return new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), {
		type: "image/webp",
		lastModified: Date.now(),
	})
}

export function createFeedbackWidget(
	container: HTMLElement,
	options: FeedbackWidgetOptions,
): FeedbackWidgetController {
	if (typeof document === "undefined") {
		return { open() {}, close() {}, destroy() {} }
	}
	if (!options.appId) throw new Error("appId is required")
	ensureStyle()

	let threadState: ThreadState | null = null
	let pollTimer: number | null = null
	let panelReady = false
	let pendingFiles: File[] = []
	let renderedMessages: FeedbackMessage[] = []
	let removeLoadListener: (() => void) | null = null
	const root = document.createElement("div")
	root.className = "yd-feedback-root"
	const launcher = document.createElement("button")
	launcher.type = "button"
	launcher.className = "yd-feedback-launcher"
	launcher.setAttribute("aria-label", options.launcherText || "反馈")
	launcher.innerHTML = FEEDBACK_ICON
	root.appendChild(launcher)
	container.appendChild(root)

	const panel = document.createElement("section")
	panel.className = "yd-feedback-panel"
	panel.setAttribute("aria-label", options.panelTitle || DEFAULT_PANEL_TITLE)
	panel.innerHTML = `
<div class="yd-feedback-header">
	<div>
		<h2 class="yd-feedback-title">${escapeHtml(options.panelTitle || DEFAULT_PANEL_TITLE)}</h2>
		<p class="yd-feedback-subtitle">${escapeHtml(options.panelSubtitle || DEFAULT_PANEL_SUBTITLE)}</p>
	</div>
	<button type="button" class="yd-feedback-close" aria-label="关闭">
		<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
	</button>
</div>
<div class="yd-feedback-shell">
	<aside class="yd-feedback-sidebar">
		<div class="yd-feedback-sidebar-toolbar">
			<div class="yd-feedback-sidebar-title">${FEEDBACK_ICON}<span>反馈记录</span></div>
			<button type="button" class="yd-feedback-icon-button" data-role="clear" aria-label="新建反馈">
				<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
			</button>
		</div>
		<div class="yd-feedback-thread-list">
			<div class="yd-feedback-thread-card" data-role="thread-card">
				<div class="yd-feedback-thread-badges">
					<span class="yd-feedback-badge" data-role="thread-category">问题反馈</span>
					<span class="yd-feedback-badge" data-status="true" data-role="thread-status">已提交</span>
				</div>
				<p class="yd-feedback-thread-preview" data-role="thread-preview"></p>
				<div class="yd-feedback-thread-meta">
					<span data-role="thread-time"></span>
					<span>我的反馈</span>
				</div>
			</div>
			<div class="yd-feedback-empty-thread" data-role="thread-empty">提交反馈后，记录会显示在这里。</div>
		</div>
	</aside>
	<div class="yd-feedback-main">
		<div class="yd-feedback-form">
			<div class="yd-feedback-row">
				<div class="yd-feedback-field">
					<label class="yd-feedback-label">反馈类型</label>
					<div class="yd-feedback-select" data-role="category-select">
						<button type="button" class="yd-feedback-select-button" data-role="category-trigger">
							<span class="yd-feedback-select-value">${BUG_ICON}<span data-role="category-value">问题反馈</span></span>
							<span class="yd-feedback-select-chevron">${CHEVRON_ICON}</span>
						</button>
						<div class="yd-feedback-select-menu" data-role="category-menu"></div>
					</div>
				</div>
			</div>
			<div class="yd-feedback-field">
				<label class="yd-feedback-label">${NOTE_ICON}<span>反馈内容</span></label>
				<div class="yd-feedback-compose-box">
					<textarea class="yd-feedback-textarea" data-role="content" placeholder="${escapeHtml(options.placeholder || "请描述问题现象、操作步骤或建议内容。")}"></textarea>
					<div class="yd-feedback-attachment-row">
						<button type="button" class="yd-feedback-file-trigger" data-role="files-trigger">${IMAGE_ICON}<span>上传图片</span></button>
						<input class="yd-feedback-file-input" type="file" accept="image/png,image/jpeg,image/webp" multiple data-role="files" />
						<div class="yd-feedback-files" data-role="files-label">${DEFAULT_ATTACHMENTS_LABEL}</div>
					</div>
				</div>
			</div>
			<div class="yd-feedback-field">
				<label class="yd-feedback-label">${MAIL_ICON}<span>联系方式（选填）</span></label>
				<input class="yd-feedback-input" data-role="contact" value="${escapeHtml(getInitialContact(options.actor))}" placeholder="${escapeHtml(options.contactPlaceholder || "邮箱或电话号码，仅用于反馈跟进")}" />
			</div>
			<div class="yd-feedback-messages" data-role="messages"></div>
			<div class="yd-feedback-error" data-role="error"></div>
			<div class="yd-feedback-actions">
				<button type="button" class="yd-feedback-secondary" data-role="clear-secondary">关闭</button>
				<button type="button" class="yd-feedback-submit" data-role="submit">提交反馈</button>
			</div>
		</div>
	</div>
</div>
`

	function initPanel() {
		if (panelReady) return
		panelReady = true
		root.appendChild(panel)
		const categorySelect = panel.querySelector<HTMLElement>(
			'[data-role="category-select"]',
		)
		const categoryMenu = panel.querySelector<HTMLElement>(
			'[data-role="category-menu"]',
		)
		const categoryValue = panel.querySelector<HTMLElement>(
			'[data-role="category-value"]',
		)
		if (categoryMenu && categoryValue) {
			const categories = options.categories || DEFAULT_CATEGORIES
			categoryValue.textContent =
				categories[0]?.label || DEFAULT_CATEGORIES[0]?.label || "反馈"
			for (const item of categories) {
				const option = document.createElement("button")
				option.type = "button"
				option.className = "yd-feedback-select-option"
				option.dataset.value = item.value
				option.dataset.selected = String(item.value === categories[0]?.value)
				option.innerHTML = `${BUG_ICON}<span>${escapeHtml(item.label)}</span>`
				option.addEventListener("click", () => {
					for (const node of categoryMenu.querySelectorAll<HTMLElement>(
						".yd-feedback-select-option",
					)) {
						node.dataset.selected = String(node === option)
					}
					categoryValue.textContent = item.label
					if (categorySelect) {
						categorySelect.dataset.value = item.value
						categorySelect.dataset.open = "false"
					}
				})
				categoryMenu.appendChild(option)
			}
			categorySelect?.setAttribute("data-value", categories[0]?.value || "BUG")
		}
		panel
			.querySelector<HTMLButtonElement>('[data-role="category-trigger"]')
			?.addEventListener("click", () => {
				if (!categorySelect) return
				categorySelect.dataset.open =
					categorySelect.dataset.open === "true" ? "false" : "true"
			})
		panel
			.querySelector<HTMLButtonElement>(".yd-feedback-close")
			?.addEventListener("click", close)
		panel
			.querySelector<HTMLButtonElement>('[data-role="clear"]')
			?.addEventListener("click", resetThread)
		panel
			.querySelector<HTMLButtonElement>('[data-role="clear-secondary"]')
			?.addEventListener("click", close)
		panel
			.querySelector<HTMLButtonElement>('[data-role="files-trigger"]')
			?.addEventListener("click", () => {
				panel.querySelector<HTMLInputElement>('[data-role="files"]')?.click()
			})
		panel
			.querySelector<HTMLInputElement>('[data-role="files"]')
			?.addEventListener("change", (event) => {
				setPendingFiles((event.target as HTMLInputElement).files)
			})
		const composeBox = panel.querySelector<HTMLElement>(
			".yd-feedback-compose-box",
		)
		composeBox?.addEventListener("dragover", (event) => {
			event.preventDefault()
		})
		composeBox?.addEventListener("drop", (event) => {
			event.preventDefault()
			setPendingFiles(event.dataTransfer?.files || null)
		})
		composeBox?.addEventListener("paste", (event) => {
			const files = Array.from(event.clipboardData?.files || []).filter(
				(file) => file.type.startsWith("image/"),
			)
			if (files.length > 0) {
				setPendingFiles(files)
			}
		})
		panel
			.querySelector<HTMLButtonElement>('[data-role="submit"]')
			?.addEventListener("click", () => void submit())
	}

	function setPendingFiles(files: FileList | File[] | null) {
		pendingFiles = Array.from(files || [])
			.filter((file) => file.type.startsWith("image/"))
			.slice(0, MAX_IMAGES_PER_MESSAGE)
		const label = panel.querySelector<HTMLElement>('[data-role="files-label"]')
		if (label) {
			label.textContent =
				pendingFiles.length > 0
					? `已选择 ${pendingFiles.length} 张图片`
					: DEFAULT_ATTACHMENTS_LABEL
		}
	}

	function resetThread() {
		threadState = null
		pendingFiles = []
		renderedMessages = []
		renderMessages([])
		updateThreadCard()
		setError("")
		const textarea = panel.querySelector<HTMLTextAreaElement>(
			'[data-role="content"]',
		)
		if (textarea) textarea.value = ""
		const fileInput = panel.querySelector<HTMLInputElement>(
			'[data-role="files"]',
		)
		if (fileInput) fileInput.value = ""
		const filesLabel = panel.querySelector<HTMLElement>(
			'[data-role="files-label"]',
		)
		if (filesLabel) filesLabel.textContent = DEFAULT_ATTACHMENTS_LABEL
	}

	function updateThreadCard() {
		const card = panel.querySelector<HTMLElement>('[data-role="thread-card"]')
		const empty = panel.querySelector<HTMLElement>('[data-role="thread-empty"]')
		if (!card || !empty) return
		if (!threadState) {
			card.dataset.visible = "false"
			empty.style.display = "block"
			return
		}
		card.dataset.visible = "true"
		empty.style.display = "none"
		const category = card.querySelector<HTMLElement>(
			'[data-role="thread-category"]',
		)
		const status = card.querySelector<HTMLElement>(
			'[data-role="thread-status"]',
		)
		const preview = card.querySelector<HTMLElement>(
			'[data-role="thread-preview"]',
		)
		const time = card.querySelector<HTMLElement>('[data-role="thread-time"]')
		if (category) {
			category.textContent = formatCategoryLabel(
				threadState.category || DEFAULT_CATEGORIES[0]?.value,
				options.categories || DEFAULT_CATEGORIES,
			)
		}
		if (status) status.textContent = formatThreadStatus(threadState.status)
		if (preview) preview.textContent = threadState.contentPreview || ""
		if (time) time.textContent = formatThreadTime(threadState.createdAt)
	}

	function setError(message: string) {
		const node = panel.querySelector<HTMLElement>('[data-role="error"]')
		if (node) node.textContent = message
		if (message) options.onError?.(new Error(message))
	}

	function setSubmitting(value: boolean) {
		const button = panel.querySelector<HTMLButtonElement>(
			'[data-role="submit"]',
		)
		if (button) button.disabled = value
	}

	function renderMessages(messages: FeedbackMessage[]) {
		const node = panel.querySelector<HTMLElement>('[data-role="messages"]')
		if (!node) return
		renderedMessages = messages
		node.innerHTML = ""
		for (const message of messages) {
			const item = document.createElement("div")
			item.className = "yd-feedback-message"
			item.dataset.admin = String(message.senderType !== "END_USER")
			item.innerHTML = `<div class="yd-feedback-message-meta">${formatSenderType(message.senderType)} · ${new Date(message.createdAt).toLocaleString()}</div><div class="yd-feedback-message-content"></div>`
			const content = item.querySelector(".yd-feedback-message-content")
			if (content) {
				content.textContent =
					message.senderType === "END_USER"
						? message.content
						: message.translatedContent || message.content
			}
			node.appendChild(item)
		}
	}

	function appendMessages(messages: FeedbackMessage[]) {
		const byId = new Map<string, FeedbackMessage>()
		for (const message of renderedMessages) byId.set(message.id, message)
		for (const message of messages) byId.set(message.id, message)
		renderMessages(Array.from(byId.values()))
	}

	async function uploadImages(files: File[]) {
		const selected = files.slice(0, MAX_IMAGES_PER_MESSAGE)
		const ids: string[] = []
		for (const file of selected) {
			const compressed = await compressImage(file)
			const body = new FormData()
			body.append("file", compressed)
			if (threadState) body.append("threadId", threadState.threadId)
			const headers: Record<string, string> = {}
			if (threadState) headers["x-feedback-token"] = threadState.clientToken
			const response = await fetch(
				`${getBaseUrl(options)}/api/v1/feedback/${encodeURIComponent(options.appId)}/resources/images`,
				{ method: "POST", body, headers },
			)
			const payload = await response.json()
			if (!response.ok || payload?.code !== 200) {
				throw new Error(payload?.message || "Image upload failed")
			}
			ids.push(payload.data.resource.id)
		}
		return ids
	}

	async function submit() {
		const content = panel
			.querySelector<HTMLTextAreaElement>('[data-role="content"]')
			?.value.trim()
		if (!content) {
			setError("请输入反馈内容。")
			return
		}
		setSubmitting(true)
		setError("")
		try {
			const category =
				panel.querySelector<HTMLElement>('[data-role="category-select"]')
					?.dataset.value || "OTHER"
			const contact =
				panel.querySelector<HTMLInputElement>('[data-role="contact"]')?.value ||
				""
			const fileInput = panel.querySelector<HTMLInputElement>(
				'[data-role="files"]',
			)
			const attachments = await uploadImages(pendingFiles)
			if (!threadState) {
				const result = await requestJson<{
					thread: { id: string; status: string }
					message: { id: string }
					clientToken: string
				}>(
					`${getBaseUrl(options)}/api/v1/feedback/${encodeURIComponent(options.appId)}/threads`,
					{
						method: "POST",
						body: safeJson({
							category,
							content,
							attachments,
							actor: {
								...(options.actor || {}),
								contactEmail: contact.includes("@")
									? contact
									: options.actor?.contactEmail,
								contactPhone:
									contact && !contact.includes("@")
										? contact
										: options.actor?.contactPhone,
							},
							metadata: collectMetadata(options),
						}),
					},
				)
				threadState = {
					threadId: result.thread.id,
					clientToken: result.clientToken,
					lastMessageId: result.message?.id,
					status: result.thread.status,
					category,
					contentPreview: content,
					createdAt: new Date().toISOString(),
				}
				updateThreadCard()
				options.onThreadCreated?.(result.thread.id)
				options.onMessageSent?.(result.message.id)
			} else {
				const result = await requestJson<{ message: { id: string } }>(
					`${getBaseUrl(options)}/api/v1/feedback/${encodeURIComponent(options.appId)}/threads/${encodeURIComponent(threadState.threadId)}/messages`,
					{
						method: "POST",
						headers: { "x-feedback-token": threadState.clientToken },
						body: safeJson({
							content,
							attachments,
							metadata: collectMetadata(options),
						}),
					},
				)
				threadState.lastMessageId = result.message.id
				threadState.contentPreview = content
				options.onMessageSent?.(result.message.id)
			}
			const textarea = panel.querySelector<HTMLTextAreaElement>(
				'[data-role="content"]',
			)
			if (textarea) textarea.value = ""
			if (fileInput) fileInput.value = ""
			pendingFiles = []
			const filesLabel = panel.querySelector<HTMLElement>(
				'[data-role="files-label"]',
			)
			if (filesLabel) filesLabel.textContent = DEFAULT_ATTACHMENTS_LABEL
			await pollOnce()
			startPolling()
		} catch (error) {
			setError(error instanceof Error ? error.message : "提交失败")
		} finally {
			setSubmitting(false)
		}
	}

	async function pollOnce() {
		if (!threadState) return
		const currentThread = threadState
		const params = new URLSearchParams()
		if (currentThread.lastMessageId) {
			params.set("afterMessageId", currentThread.lastMessageId)
		}
		params.set("feedbackToken", currentThread.clientToken)
		const result = await requestJson<{
			threadStatus: string
			messages: FeedbackMessage[]
			nextPollAfterMs: number
		}>(
			`${getBaseUrl(options)}/api/v1/feedback/${encodeURIComponent(options.appId)}/threads/${encodeURIComponent(currentThread.threadId)}/messages?${params.toString()}`,
		)
		currentThread.status = result.threadStatus
		updateThreadCard()
		if (result.messages.length > 0) {
			const latestMessage = result.messages.at(-1)
			if (latestMessage) currentThread.lastMessageId = latestMessage.id
			appendMessages(result.messages)
		}
		if (result.threadStatus !== "WAITING_ADMIN") stopPolling()
		else schedulePoll(result.nextPollAfterMs)
	}

	function schedulePoll(delay: number) {
		stopPolling()
		if (!delay || delay < 1 || document.hidden || !threadState) return
		pollTimer = window.setTimeout(() => void pollOnce(), delay)
	}

	function startPolling() {
		if (!threadState || threadState.status !== "WAITING_ADMIN") return
		schedulePoll(10_000)
	}

	function stopPolling() {
		if (pollTimer) window.clearTimeout(pollTimer)
		pollTimer = null
	}

	function open() {
		initPanel()
		panel.dataset.open = "true"
		window.setTimeout(() => {
			panel.querySelector<HTMLTextAreaElement>('[data-role="content"]')?.focus()
		}, 0)
	}

	function close() {
		panel.dataset.open = "false"
		stopPolling()
	}

	launcher.addEventListener("click", open)
	const handleVisibilityChange = () => {
		if (document.hidden) stopPolling()
		else startPolling()
	}
	const handleDocumentClick = (event: MouseEvent) => {
		const categorySelect = panel.querySelector<HTMLElement>(
			'[data-role="category-select"]',
		)
		if (
			categorySelect &&
			event.target instanceof Node &&
			!categorySelect.contains(event.target)
		) {
			categorySelect.dataset.open = "false"
		}
	}
	document.addEventListener("visibilitychange", handleVisibilityChange)
	document.addEventListener("click", handleDocumentClick)

	if (typeof window !== "undefined") {
		const preload = () => {
			if ("requestIdleCallback" in window) {
				;(window as any).requestIdleCallback(() => initPanel())
			} else {
				globalThis.setTimeout(() => initPanel(), 1200)
			}
		}
		if (document.readyState === "complete") preload()
		else {
			window.addEventListener("load", preload, { once: true })
			removeLoadListener = () => window.removeEventListener("load", preload)
		}
	}

	return {
		open,
		close,
		destroy() {
			stopPolling()
			document.removeEventListener("visibilitychange", handleVisibilityChange)
			document.removeEventListener("click", handleDocumentClick)
			removeLoadListener?.()
			root.remove()
		},
	}
}

export function mountFeedbackWidget(
	container: HTMLElement,
	options: FeedbackWidgetOptions,
) {
	return createFeedbackWidget(container, options)
}
